import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Publicite, PubService } from '../../services/Pub.service';
import { Book, BookService } from '../../services/BookService.service';
import { TranslatePipe } from '@ngx-translate/core';
import { VideoModalComponent } from '../../shared/video-modal/video-modal.component';
import { forkJoin, of } from 'rxjs';
import { NotificationService } from '../../services/notification.service';
import {
  MessagerieService,
  Conversation,
} from '../../services/messagerie.service';
import { map } from 'rxjs/operators';
import { OrgconfService } from '../../services/orgconf.service';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { Rafi9niService } from '../../services/rafi9ni.service';

@Component({
  selector: 'app-accueil',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    TranslatePipe,
    VideoModalComponent,
    LoaderComponent,
  ],
  templateUrl: './accueil.component.html',
  styleUrls: ['./accueil.component.css'],
})
export class AccueilComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('carousel') carousel!: ElementRef;
  @Output() sidebarToggle = new EventEmitter<void>();

  slides: HTMLElement[] = [];
  currentIndex = 0;
  private autoplayInterval: any;
  loading = false;
  sidebarOpen = true; // Sidebar open by default
  isVideoModalOpen = false;
  videoApiUrl: string | null = null;

  publicites: Publicite[] = [];
  books: Book[] = [];
  notificationCounts: { [key: string]: number } = {}; 
  showDelice: boolean = false;
  showEClass: boolean = false;
  showRafi9ni: boolean = false;
  totalUnreadMessages: number = 0; // To store total unread messages count
  mixedSlides: {
    type: 'book' | 'pub';
    imageUrl: string;
    id?: number;
    lien?: string;
  }[] = [];

  constructor(
    private pubService: PubService,
    private bookService: BookService,
    private notificationService: NotificationService, // Inject NotificationService
    private messagerieService: MessagerieService, // Inject MessagerieService
    private orgconf: OrgconfService,
    private rafi9niService: Rafi9niService // Inject Rafi9niService
  ) {}

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
    this.sidebarToggle.emit();
  }

  onSidebarToggle(isOpen: boolean) {
    this.sidebarOpen = isOpen;
  }

  closeVideoModal() {
    this.isVideoModalOpen = false;
    this.videoApiUrl = null;
  }

  ngOnInit() {
    // Show video modal after login with dynamic VideoAcceuil publicite
    this.loading = true;
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('showVideoModal') === 'true'
    ) {
      this.pubService
        .getPublicites('Pubeducanet', 'VideoAcceuil', 1, 1)
        .subscribe({
          next: (res: any) => {
            const list: Publicite[] =
              res?.results?.articlePublicitaire || res || [];
            const first = list[0];

            if (first?.pathvideo || first?.href) {
              this.videoApiUrl = first?.pathvideo || first?.href || '';
              this.isVideoModalOpen = true;
            } else {
              this.isVideoModalOpen = false;
              this.videoApiUrl = null;
            }
          },
          error: (error) => {
            console.error('Error fetching video content:', error);
            this.isVideoModalOpen = false;
            this.videoApiUrl = null;
          },
          complete: () => {
            localStorage.removeItem('showVideoModal');
          },
        });
    }

    const userDataStr = typeof localStorage !== 'undefined' ? localStorage.getItem('userData') : null;
    let iduser: string | null = null;

    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        iduser = userData.iduser ? String(userData.iduser) : null;
      } catch (e) {
        console.error('Error parsing userData from localStorage', e);
      }
    }

    // Read children list to compute unread messages per child (idUserStudent)
    let students: any[] = [];
    const studentListStr = typeof localStorage !== 'undefined' ? localStorage.getItem('studentList') : null;
    if (studentListStr) {
      try {
        students = JSON.parse(studentListStr) || [];
      } catch (e) {
        console.error('Error parsing studentList from localStorage', e);
      }
    }

    // Build conversations observable: fetch for each child and flatten
    let conversations$ = of([] as Conversation[]);
    if (students.length > 0) {
      const obsList = students
        .filter((s: any) => s && s.idUserStudent)
        .map((s: any) =>
          this.messagerieService.getConversations(Number(s.idUserStudent))
        );

      if (obsList.length > 0) {
        conversations$ = forkJoin(obsList).pipe(
          map((arrays: Conversation[][]) => arrays.flat())
        );
      }
    }

    // refresh configuration whenever accueil is initialized
    this.orgconf.refreshConfig().subscribe();

    // read delice configuration early (updates automatically when refreshConfig emits)
    this.orgconf.isDeliceEnabled().subscribe(enabled => {
      this.showDelice = enabled;
    });

    forkJoin({
      books: this.bookService.getBooks(),
      pubs: this.pubService.getPublicites('Pubeducanet', 'StorieWeb', 1, 100),
      notifications: iduser
        ? this.notificationService.getNotificationCounts(iduser)
        : of({}),
      conversations: conversations$,
    }).subscribe({
      next: ({ books, pubs, notifications, conversations }: any) => {
        this.books = books.listBookModel || [];
        this.publicites = pubs.results?.articlePublicitaire || [];
        // remove all publicites when delice feature disabled
        if (!this.showDelice) {
          this.publicites = [];
        }
        this.notificationCounts = notifications?.response || {};
        this.calculateTotalUnreadMessages(conversations);
        this.publicites.forEach((pub, index) => {});
        this.buildMixedSlides();
        setTimeout(() => this.initCarousel(), 0);
        this.loading = false;
      },
    });
    this.buildMixedSlides();

    // Check if E-class icon should be displayed (only when idOrg === 1 or magicland)
    this.checkEClassOrgAccess();

    // Check if rafi9ni is available for this guardian's children
    this.checkRafi9niAccess();

    setTimeout(() => this.initCarousel(), 0);
  }

  checkEClassOrgAccess() {
    try {
      // 1. Check studentList from localStorage
      const studentListStr = typeof localStorage !== 'undefined' ? localStorage.getItem('studentList') : null;
      if (studentListStr) {
        const students = JSON.parse(studentListStr);
        if (Array.isArray(students)) {
          const match = students.some((s: any) => 
            Number(s?.idOrg) === 1 || 
            Number(s?.idorg) === 1 || 
            String(s?.nameOrg || '').toLowerCase() === 'magicland' ||
            String(s?.nameorg || '').toLowerCase() === 'magicland'
          );
          if (match) {
            this.showEClass = true;
            return;
          }
        }
      }

      // 2. Check user / userData from localStorage
      const userStr = typeof localStorage !== 'undefined' 
        ? (localStorage.getItem('userData') || localStorage.getItem('user') || localStorage.getItem('connectedUser'))
        : null;
      if (userStr) {
        const u = JSON.parse(userStr);
        if (
          Number(u?.idOrg) === 1 || 
          Number(u?.idorg) === 1 || 
          Number(u?.idOrganization) === 1 ||
          String(u?.nameOrg || '').toLowerCase() === 'magicland' ||
          String(u?.nameorg || '').toLowerCase() === 'magicland'
        ) {
          this.showEClass = true;
          return;
        }
      }
    } catch (e) {
      console.error('Error checking idOrg for E-class access', e);
    }
    // Only display E-class if org matches idOrg = 1 / magicland
    this.showEClass = false;
  }

  /**
   * Affiche la tuile Rafi9ni uniquement si au moins un enfant a accès à Rafi9ni.
   * Détection : champs `digiref` présent dans studentList (si renseigné),
   * sinon via GetGradeById(gradeId) > 0.
   */
  private checkRafi9niAccess(): void {
    this.showRafi9ni = false;
    try {
      const studentListStr =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('studentList')
          : null;
      if (!studentListStr) return;

      const students = JSON.parse(studentListStr);
      if (!Array.isArray(students) || students.length === 0) return;

      // 1. if digiref is already provided for the students, use it
      const withDigiref = students.filter(
        (s: any) => s && s.digiref !== undefined && s.digiref !== null
      );
      if (withDigiref.length > 0) {
        this.showRafi9ni = withDigiref.some((s: any) => Number(s.digiref) > 0);
        return;
      }

      // 2. otherwise ask the API for the first student with a gradeId
      const target = students.find((s: any) => s && Number(s.gradeId) > 0);
      if (!target) return;

      this.rafi9niService.getGradeById(Number(target.gradeId)).subscribe({
        next: (res: any) => {
          const ref = Number(String(res ?? '').trim());
          this.showRafi9ni = !isNaN(ref) && ref > 0;
        },
        error: () => {
          this.showRafi9ni = false;
        },
      });
    } catch (e) {
      console.error('Error checking Rafi9ni access', e);
      this.showRafi9ni = false;
    }
  }

  getSlides() {
    const slides: {
      type: 'book' | 'pub';
      imageUrl: string;
      id?: number;
      lien?: string;
    }[] = [];
    this.books.forEach((book, i) => {
      slides.push({
        type: 'book',
        imageUrl: book.url_image,
        id: book.id,
      });
      if (i % 2 === 1 && this.publicites[i / 2]) {
        const pub = this.publicites[i / 2];
        slides.push({
          type: 'pub',
          imageUrl: pub.pathimage,
          lien: pub.href,
        });
      }
    });

    return slides;
  }

  buildMixedSlides() {
    this.mixedSlides = [];
    let pubIndex = 0;

    for (let i = 0; i < this.books.length; i += 2) {
      if (i < this.books.length) {
        this.mixedSlides.push({
          type: 'book',
          imageUrl: this.books[i].url_image,
          id: this.books[i].id,
        });
      }

      if (i + 1 < this.books.length) {
        this.mixedSlides.push({
          type: 'book',
          imageUrl: this.books[i + 1].url_image,
          id: this.books[i + 1].id,
        });
      }

      // only insert pub slides if feature enabled
      if (this.showDelice && this.publicites.length > 0) {
        const pub = this.publicites[pubIndex % this.publicites.length];
        this.mixedSlides.push({
          type: 'pub',
          imageUrl: pub.pathimage,
          lien: pub.href,
        });
        pubIndex++;
      }
    }
  }

  loadPubs() {
    this.pubService
      .getPublicites('Pubeducanet', 'StorieWeb', 1, 100)
      .subscribe({
        next: (res: any) => {
          this.publicites = res.results?.articlePublicitaire || [];

          this.buildMixedSlides();

          setTimeout(() => this.initCarousel(), 0);
        },
      });
  }

  ngAfterViewInit() {}

  initCarousel() {
    if (!this.carousel) return;

    this.slides = Array.from(
      this.carousel.nativeElement.querySelectorAll('.slide')
    );
    if (this.slides.length === 0) return;

    this.startAutoplay();
  }

  updateSlides() {
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    this.carousel.nativeElement.style.transition = 'transform 0.5s ease';
    this.carousel.nativeElement.style.transform = `translateX(-${
      this.currentIndex * slideWidth
    }px)`;
  }

  moveCarousel(direction: number) {
    const carouselEl = this.carousel.nativeElement as HTMLElement;
    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const slidesToMove = 3;

    carouselEl.style.transition =
      'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    carouselEl.style.transform = `translateX(-${
      direction * slidesToMove * slideWidth
    }px)`;

    setTimeout(() => {
      carouselEl.style.transition = 'none';

      for (let i = 0; i < slidesToMove; i++) {
        if (direction === 1) {
          const first = this.slides.shift()!;
          carouselEl.appendChild(first);
          this.slides.push(first);
        } else {
          const last = this.slides.pop()!;
          carouselEl.insertBefore(last, this.slides[0]);
          this.slides.unshift(last);
        }
      }

      carouselEl.style.transform = 'translateX(0)';
    }, 500);
  }

  goToSlide(slide: {
    type: 'book' | 'pub';
    imageUrl: string;
    id?: number;
    lien?: string;
  }) {
    if (slide.type === 'book' && slide.id) {
      const book = this.books.find((b) => b.id === slide.id);
      if (book) {
        window.open(book.url_book, '_blank');
      }
    } else if (slide.type === 'pub' && slide.lien) {
      window.open(slide.lien, '_blank');
    }
  }

  startAutoplay() {
    if (this.autoplayInterval) clearInterval(this.autoplayInterval);

    this.autoplayInterval = setInterval(() => {
      if (!this.slides || this.slides.length === 0) return;

      this.moveCarousel(1);
    }, 5000); //
  }

  ngOnDestroy() {
    if (this.autoplayInterval) clearInterval(this.autoplayInterval);
  }

  private calculateTotalUnreadMessages(conversations: Conversation[]): void {
    this.totalUnreadMessages = conversations.reduce((total, conversation) => {
      return total + (conversation.unreadCount || 0);
    }, 0);
  }
}
